import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { nanoid } from 'nanoid'
import { uiStore } from 'shared/store/uiStore'
import { CAMPAIGN_NAME_LIMIT } from 'shared/constants/limits'
import { toastStore } from 'shared/ui'
import {
  CampaignApi,
  type IResponseFullCampaign,
  type IParamsCreateCampaign,
  type IResponseCampaignStatus,
} from 'entities/Campaign'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { NameInputStore } from 'features/CreateOrEdit/NameInput'
import { showWebhookActionModal } from 'widgets/EnrollByWebhook/actions/webhookAction/webhookAction.tsx'
import { CampaignBlockerStore } from 'pages/campaigns/ui/CampaignBlocker'
import { CampaignEnrollContactsStore } from 'pages/campaigns/ui/CampaignEnrollContacts'
import { CampaignMessagesStore } from 'pages/campaigns/ui/CampaignMessages'
import { CampaignSettingsStore } from 'pages/campaigns/ui/CampaignSettings'
import { CampaignRoutes } from 'pages/campaigns/types'
import { CampaignEnrolmentStore } from 'pages/campaigns/ui/CampaignEnrolment'
import { openDisableCampaignModal } from 'pages/campaigns/actions/actionItems/openDisableCampaignModal'

export class CampaignViewStore {
  nameInputStore
  campaignSettingsStore
  campaignBlockerStore
  campaignEnrollContactsStore
  campaignMessagesStore
  status: IResponseCampaignStatus | null = null

  constructor(
    private _entities: IEntitiesStore,
    private response?: IResponseFullCampaign,
    public isDuplicate = false
  ) {
    this.nameInputStore = new NameInputStore({
      focusOnMount: !response || isDuplicate,
      limit: CAMPAIGN_NAME_LIMIT,
      isResetErrorOnChange: true,
    })
    this.campaignSettingsStore = new CampaignSettingsStore(this._entities)
    this.campaignEnrollContactsStore = new CampaignEnrollContactsStore(
      this._entities,
      this.campaignSettingsStore,
      response ? (isDuplicate ? { ...response, webhook_url: undefined } : response) : undefined
    )
    this.campaignMessagesStore = new CampaignMessagesStore(this._entities)
    this.campaignBlockerStore = new CampaignBlockerStore(this)
    this._entities.inboxesStore.fetchInboxes()

    if (response) {
      this.init(response)
    } else {
      this.campaignSettingsStore.init({
        unenroll_on_response: false,
        allow_re_entering: false,
      })
    }

    makeAutoObservable(this)
  }

  dispose = () => {
    this.campaignMessagesStore.dispose()
    this.campaignEnrollContactsStore.dispose()
    this.campaignSettingsStore.dispose()
  }

  get params(): IParamsCreateCampaign {
    return {
      name: this.nameInputStore.name,
      filters: this.campaignEnrollContactsStore.paramsFilters,
      exclude_filters: this.campaignEnrollContactsStore.paramsExcludeFilters,
      settings: {
        ...this.campaignSettingsStore.params,
        enroll_by_webhook: this.campaignEnrollContactsStore.enrollByWebhookStore.isActive,
      },
      steps: this.campaignMessagesStore.params,
      status: this.isPublish ? 'active' : 'draft',
      default_timezone: uiStore.timezone,
    }
  }

  init = async (response: IResponseFullCampaign) => {
    this.status = response.status
    if (this.isDuplicate) {
      this.nameInputStore.init(`Copy of: ${response.name}`)
    } else {
      this.nameInputStore.init(response.name)
    }

    await Promise.all([
      this._entities.integrationsStore.fetchIntegrations(),
      this._entities.mergeFieldStore.loadAllFields(),
    ])

    if (response.settings) {
      this.campaignSettingsStore.init(response.settings)
    }

    await Promise.all([
      this.campaignMessagesStore.init(response.steps, response),
      this.campaignEnrollContactsStore.init(response.filters, response.exclude_filters),
    ])
    this.campaignBlockerStore.updateCache()
  }

  get isEdit() {
    return !!this.response && !this.isDuplicate
  }

  get disabledSave() {
    return this.isEdit && !this.campaignBlockerStore.isExistChanges
  }

  get isActive() {
    return this.status === 'active'
  }

  isPublish = false
  onPublish = async (isPublished?: boolean) => {
    const isValid = this._checkValidate()
    if (isValid) {
      const data = await this._onSave({ ...this.params, status: 'active' })
      if (!data) {
        toastStore.add({
          title: 'Failed to activate',
          desc: 'Complete all required fields',
          type: 'error',
        })
        return
      }
      runInAction(() => {
        this.isPublish = data.data.status === 'active'
        this.campaignBlockerStore.updateCache()
      })
      const campaignEnrolmentStore = new CampaignEnrolmentStore()
      await campaignEnrolmentStore.init({
        data: {
          name: data.data.name,
          id: data.data.id,
          filters: data.data.filters,
          exclude_filters: data.data.exclude_filters,
          isPublished: isPublished,
        },
        onNext: () => {
          setTimeout(() => {
            uiStore.changeRoute({
              path: `${CampaignRoutes.root}/${CampaignRoutes.all}`,
            })
            if (data.data.settings.enroll_by_webhook && data.data.webhook_url) {
              showWebhookActionModal({
                webhookUrl: data.data.webhook_url,
                sourceName: data.data.name,
                source: 'campaign',
              })
            }
          })
        },
      })
    } else {
      this.setValidateTrigger()
    }
  }

  loadingSave = false
  private _onSave = async (params: IParamsCreateCampaign, withError?: boolean) => {
    try {
      this.loadingSave = true
      const { data } =
        this.isEdit && this.response?.id
          ? await CampaignApi.update(this.response?.id, params)
          : await CampaignApi.create(params)

      this.campaignBlockerStore.updateCache()
      return data
    } catch (e) {
      if (e instanceof AxiosError) {
        this.setValidateTrigger()
        // steps.0.payload.message, steps.0.send_from
        e.errors.entries().forEach(([field, error]) => {
          if (field === 'filters') {
            this.campaignEnrollContactsStore.setError(error.value)
          }
          if (field === 'name') {
            this.nameInputStore.setError(error.value)
          }
          if (field.startsWith('steps')) {
            const path = field.split('.')
            const stepIndex = Number(path[1])
            if (!isNaN(stepIndex)) {
              const message = this.campaignMessagesStore.messages[stepIndex]
              if (message) {
                message.setError(path.slice(2), error.value)
              }
            }
          }
        })

        if (withError) {
          throw e
        }
      }
    } finally {
      runInAction(() => {
        this.loadingSave = false
      })
    }
  }

  onSave = async () => {
    const isValid = this.isPublish ? this._checkValidate() : this._checkValidateDraft()
    if (isValid) {
      const data = await this._onSave(this.params)
      if (!data) {
        return
      }
      if (!this.isEdit) {
        uiStore.changeRoute({
          path: `${CampaignRoutes.root}/${CampaignRoutes.edit}/${data.data.id}`,
        })
      }
      toastStore.add({
        type: 'info',
        title: 'Campaign saved',
      })
    } else {
      this.setValidateTrigger()
    }
  }

  onSaveDraft = async () => {
    const isValid = this._checkValidateDraft()
    if (isValid) {
      await this._onSave({ ...this.params, status: 'draft' }, true)
    }
  }

  private _checkValidate = () => {
    let validate = true
    if (!this.nameInputStore.name) {
      validate = false
      this.nameInputStore.setError('Please enter campaign name')
    }
    if (
      this.campaignEnrollContactsStore.isEmpty &&
      !this.campaignEnrollContactsStore.enrollByWebhookStore.isActive
    ) {
      validate = false
      if (this.campaignEnrollContactsStore.isEmpty) {
        this.campaignEnrollContactsStore.setError('Please specify enrollment criteria')
      }
    }
    if (!this.campaignMessagesStore.checkValidate()) {
      validate = false
    }
    return validate
  }
  private _checkValidateDraft = () => {
    let validate = true
    if (!this.nameInputStore.name) {
      validate = false
      this.nameInputStore.setError('Please enter campaign name')
    }
    return validate
  }

  onSettings = () => {
    this.campaignSettingsStore.openModal()
  }

  validateTrigger = ''
  setValidateTrigger = () => {
    this.validateTrigger = nanoid()
  }

  openDisableModal = () => {
    if (!this.response?.id) return

    openDisableCampaignModal(this.response.id, (data) => {
      runInAction(() => {
        this.status = data.status
      })
      toastStore.add({
        type: 'info',
        title: 'Campaign disabled',
      })
    })
  }
}
